import { useEffect, useState, useCallback } from 'react'
import { Tabs, useRouter, usePathname, Redirect } from 'expo-router'
import { LayoutDashboard, Mail, FileText, MessageSquare, Settings } from 'lucide-react-native'
import { useAuth } from '../../src/auth/AuthProvider'
import { apiClient } from '../../src/api/client'
import { colors, typography } from '../../src/theme'
import { View, Text, ActivityIndicator, StyleSheet, AppState, Pressable, Dimensions, Platform } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useResponsive } from '../../src/hooks/useResponsive'
import DesktopSidebar from '../../src/components/DesktopSidebar'
import MobileHeader from '../../src/components/MobileHeader'
import MobileSidebar from '../../src/components/MobileSidebar'
import FeedbackButton from '../../src/components/FeedbackButton'
import { appEvents } from '../../src/lib/events'
import { filterForInbox } from '../../src/utils/threading'
import { haptics } from '../../src/utils/haptics'
import { clearAllNotifications, setBadgeCount } from '../../src/notifications'
import Animated, { useSharedValue, useAnimatedStyle, withTiming, ZoomIn, type SharedValue } from 'react-native-reanimated'
import type { BottomTabBarProps } from '@react-navigation/bottom-tabs'

const TAB_COUNT = 5
const INDICATOR_WIDTH = 40

function TabItem({
  route,
  isFocused,
  options,
  navigation,
  scale,
}: {
  route: any
  isFocused: boolean
  options: any
  navigation: any
  scale: SharedValue<number>
}) {
  const color = isFocused ? colors.primary : colors.text.muted

  const onPress = () => {
    haptics.selection()
    const event = navigation.emit({
      type: 'tabPress',
      target: route.key,
      canPreventDefault: true,
    })
    if (!isFocused && !event.defaultPrevented) {
      navigation.navigate(route.name, route.params)
    }
  }

  const onLongPress = () => {
    navigation.emit({
      type: 'tabLongPress',
      target: route.key,
    })
  }

  const iconScaleStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
  }))

  return (
    <Pressable
      accessibilityRole="tab"
      accessibilityState={{ selected: isFocused }}
      accessibilityLabel={options.tabBarAccessibilityLabel}
      onPress={onPress}
      onLongPress={onLongPress}
      style={styles.tabItem}
    >
      <Animated.View style={iconScaleStyle}>
        {options.tabBarIcon?.({ color, size: 24, focused: isFocused })}
      </Animated.View>
      <Text style={[styles.tabLabel, { color }]}>
        {options.title ?? route.name}
      </Text>
    </Pressable>
  )
}

function AnimatedTabBar({ state, descriptors, navigation }: BottomTabBarProps) {
  const indicatorX = useSharedValue(0)
  const scale0 = useSharedValue(1)
  const scale1 = useSharedValue(1)
  const scale2 = useSharedValue(1)
  const scale3 = useSharedValue(1)
  const scale4 = useSharedValue(1)
  const tabScales = [scale0, scale1, scale2, scale3, scale4]
  const [tabBarWidth, setTabBarWidth] = useState(Dimensions.get('window').width)

  const activeRoute = state.routes[state.index]

  // Filter to only visible routes (exclude hidden tabs like regninger)
  const HIDDEN_TABS = ['regninger']
  const visibleRoutes = state.routes.filter((route) => !HIDDEN_TABS.includes(route.name))

  const tabWidth = tabBarWidth / visibleRoutes.length

  // Find active index among visible routes
  const visibleIndex = visibleRoutes.findIndex((r) => r.key === activeRoute?.key)

  // Update indicator + scales when active index changes
  useEffect(() => {
    if (visibleIndex < 0) return

    const targetX = visibleIndex * tabWidth + (tabWidth - INDICATOR_WIDTH) / 2
    indicatorX.value = withTiming(targetX, { duration: 250 })

    tabScales.forEach((scale, i) => {
      scale.value = withTiming(i === visibleIndex ? 1.1 : 1.0, { duration: 250 })
    })
  }, [visibleIndex, tabWidth])

  const indicatorStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: indicatorX.value }],
  }))

  return (
    <View
      accessibilityRole="tablist"
      style={styles.tabBar}
      onLayout={(e) => setTabBarWidth(e.nativeEvent.layout.width)}
    >
      {/* Sliding indicator */}
      <Animated.View style={[styles.indicator, indicatorStyle]} />

      {/* Tab items — only visible routes */}
      {visibleRoutes.map((route, index) => {
        const { options } = descriptors[route.key]
        const isFocused = route.key === activeRoute?.key

        return (
          <TabItem
            key={route.key}
            route={route}
            isFocused={isFocused}
            options={options}
            navigation={navigation}
            scale={tabScales[index]}
          />
        )
      })}
    </View>
  )
}

export default function TabLayout() {
  const { user, isAuthenticated, isLoading } = useAuth()
  const router = useRouter()
  const pathname = usePathname()
  const [unreadCount, setUnreadCount] = useState(0)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { isDesktop } = useResponsive()

  // Close sidebar on route change
  useEffect(() => {
    setSidebarOpen(false)
  }, [pathname])

  const fetchUnread = useCallback(async () => {
    if (!isAuthenticated) return
    try {
      const res = await apiClient.inbox.list({ limit: '50' })
      // Use the same filter as the inbox page for consistency
      const msgs = filterForInbox(res.data?.messages || [])
      // Only count inbound unread messages that actually need user attention
      const inboundUnread = msgs.filter(
        (m: any) => m.direction === 'inbound' && m.status === 'unread' &&
          // Exclude fuldmagt confirmations and auto-handled responses
          m.parsed_data?.classification !== 'fuldmagt_confirmation' &&
          m.parsed_data?.email_type !== 'fuldmagt'
      ).length
      setUnreadCount(inboundUnread)
      setBadgeCount(inboundUnread).catch(() => {})
    } catch {}
  }, [isAuthenticated])

  // Fetch unread on mount + when app becomes active.
  // Order: clear OS tray + badge first, fire-and-forget mark-read to backend,
  // then fetchUnread which overwrites the badge with the real DB count.
  useEffect(() => {
    if (!isAuthenticated) return
    clearAllNotifications()
    apiClient.notifications.markAllRead().catch(() => {})
    fetchUnread()
    const sub = AppState.addEventListener('change', (state) => {
      if (state === 'active') {
        clearAllNotifications()
        apiClient.notifications.markAllRead().catch(() => {})
        fetchUnread()
      }
    })
    // Refresh every 60s
    const interval = setInterval(fetchUnread, 60000)
    return () => { sub.remove(); clearInterval(interval) }
  }, [isAuthenticated, fetchUnread])

  // Listen for inbox changes (mark as read, archive, etc.) — matches Next.js window event listener
  useEffect(() => {
    if (!isAuthenticated) return
    const unsub = appEvents.on('inbox-updated', () => fetchUnread())
    return unsub
  }, [isAuthenticated, fetchUnread])

  // Onboarding gate: redirect if not onboarded (skip if already on onboarding — matches Next.js)
  useEffect(() => {
    if (!isLoading && isAuthenticated && user && !user.onboarding_completed) {
      if (!pathname?.startsWith('/onboarding')) {
        router.replace('/onboarding' as any)
      }
    }
  }, [isLoading, isAuthenticated, user?.onboarding_completed, pathname])

  if (isLoading) {
    return (
      <View style={styles.loading}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    )
  }

  if (!isAuthenticated) {
    return (
      <View style={styles.loading}>
        <ActivityIndicator size="large" color={colors.primary} />
        <Redirect href="/(auth)/login" />
      </View>
    )
  }

  return (
    <View style={{ flex: 1, flexDirection: isDesktop ? 'row' : 'column' }}>
      {isDesktop && <DesktopSidebar unreadCount={unreadCount} />}
      {!isDesktop && (
        <SafeAreaView edges={['top']} style={{ backgroundColor: colors.background }}>
          <MobileHeader
            sidebarOpen={sidebarOpen}
            onToggleSidebar={() => setSidebarOpen(prev => !prev)}
          />
        </SafeAreaView>
      )}
      <View style={{ flex: 1 }}>
        <Tabs
          tabBar={(props) => {
            if (isDesktop) return null
            const activeRoute = props.state.routes[props.state.index]
            if (sidebarOpen) return null
            return <AnimatedTabBar {...props} />
          }}
          screenOptions={{
            headerShown: false,
            lazy: true,
            animation: 'fade',
          }}
        >
      <Tabs.Screen
        name="index"
        options={{
          title: 'Oversigt',
          tabBarAccessibilityLabel: 'Oversigt',
          tabBarIcon: ({ color, size }) => (
            <LayoutDashboard size={size} color={color} />
          ),
        }}
      />
      <Tabs.Screen
        name="inbox"
        options={{
          title: 'Indbakke',
          tabBarAccessibilityLabel: 'Indbakke',
          tabBarIcon: ({ color, size }) => (
            <View>
              <Mail size={size} color={color} />
              {unreadCount > 0 && (
                <Animated.View entering={ZoomIn.duration(200).springify()} style={styles.badge}>
                  <Text style={styles.badgeText}>{unreadCount > 99 ? '99+' : unreadCount}</Text>
                </Animated.View>
              )}
            </View>
          ),
        }}
      />
      <Tabs.Screen
        name="quotes"
        options={{
          title: 'Tilbud',
          tabBarAccessibilityLabel: 'Tilbud',
          tabBarIcon: ({ color, size }) => (
            <FileText size={size} color={color} />
          ),
        }}
      />
      <Tabs.Screen
        name="chat"
        options={{
          title: 'Chat',
          tabBarAccessibilityLabel: 'Chat',
          tabBarIcon: ({ color, size }) => (
            <MessageSquare size={size} color={color} />
          ),
        }}
      />
      <Tabs.Screen
        name="settings"
        options={{
          title: 'Indstillinger',
          tabBarAccessibilityLabel: 'Indstillinger',
          tabBarIcon: ({ color, size }) => (
            <Settings size={size} color={color} />
          ),
        }}
      />
      {/* Hidden tab — accessible via navigation but not shown in tab bar */}
      <Tabs.Screen
        name="regninger"
        options={{ href: null }}
      />
        </Tabs>
        {!sidebarOpen && <FeedbackButton screen="tabs" />}
        {!isDesktop && (
          <MobileSidebar
            visible={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            unreadCount={unreadCount}
          />
        )}
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  loading: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.background,
  },
  badge: {
    position: 'absolute',
    top: -4,
    right: -8,
    minWidth: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: colors.error,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 3,
  },
  badgeText: {
    color: colors.text.inverse,
    fontSize: 11,
    fontWeight: '700',
    lineHeight: 13,
  },
  tabBar: {
    flexDirection: 'row',
    borderTopWidth: 1,
    borderTopColor: colors.border,
    backgroundColor: colors.background,
    height: Platform.OS === 'web' ? 64 : 84,
    paddingBottom: Platform.OS === 'web' ? 8 : 28,
    zIndex: 40,
    paddingTop: 8,
    position: 'relative',
  },
  indicator: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: INDICATOR_WIDTH,
    height: 3,
    backgroundColor: colors.primary,
    borderRadius: 2,
  },
  tabItem: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
  },
  tabLabel: {
    fontSize: 12,
    fontWeight: '500',
    letterSpacing: 0.2,
    marginTop: 2,
  },
})
